import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { InputWrapper, LabelStyle } from "../../Common/styles/form";
import { MIN_REASON_LENGTH } from "./idempotency";

/**
 * The confirmation dialog used for every action that needs a reason:
 * reversal, unblinding, a blinding-level change, a list extension, a strata
 * generation, an MRT edit. Spec 15, 21.3, 21.8.
 *
 * WHY IT IS ONE COMPONENT. Six screens need the same three things and each one
 * of them is a requirement rather than a convenience:
 *
 *  - **Ten characters minimum.** Not arbitrary. It is the bar that stops "ok",
 *    "fix" and "." — reasons that make an audit trail formally complete and
 *    useless when somebody has to explain to a regulator why a live
 *    randomization was reversed. Enforced here AND in the DTO AND in
 *    `buildAuditRow`, because each layer can be reached without the others.
 *  - **The button disables while in flight** (spec 21.8: no optimistic
 *    updates anywhere in this module). A second click on Reverse would retire
 *    a second randomization number.
 *  - **Focus trap and a label.** MUI `Dialog` traps focus and
 *    `aria-labelledby` wires the title to it, which spec 21.8 requires of
 *    every dialog in the module.
 *
 * @category Randomization
 */

export interface ReasonDialogProps {
  open: boolean;
  title: string;
  body?: string;
  /** Rendered above the reason field: the specific thing being confirmed. */
  detail?: React.ReactNode;
  confirmLabel: string;
  /** Offered when the action has a coded reason as well as free text. */
  reasonCodes?: { value: string; label: string }[];
  reasonLabel: string;
  /**
   * Extra single-line field, e.g. the safety report reference.
   *
   * `requiredForReasonCode` makes it appear, and be required, only for that
   * one coded reason. That is not cosmetic: the unblinding table carries
   * `randomization_unblinding_events_susar_chk`, which refuses a
   * `susar_report` row with a null `safety_reference`, so without this the
   * user types a justification and is then handed a raw constraint violation.
   *
   * `maxLength` must match the column and the DTO - it defaults to 200, which
   * is wrong for `safety_reference` (120). A field that accepts more than the
   * server does just moves the rejection later.
   */
  extraField?: {
    label: string;
    name: string;
    maxLength?: number;
    helperText?: string;
    requiredForReasonCode?: string;
  };
  submitting: boolean;
  /** Server-side message, already translated by the caller. */
  errorMessage?: string | null;
  onCancel: () => void;
  onConfirm: (result: {
    reason: string;
    reasonCode?: string;
    extra?: string;
  }) => void;
}

export const ReasonDialog = ({
  open,
  title,
  body,
  detail,
  confirmLabel,
  reasonCodes,
  reasonLabel,
  extraField,
  submitting,
  errorMessage,
  onCancel,
  onConfirm,
}: ReasonDialogProps) => {
  const { t } = useTranslation("randomization");
  const [reason, setReason] = useState("");
  const [reasonCode, setReasonCode] = useState(reasonCodes?.[0]?.value ?? "");
  const [extra, setExtra] = useState("");
  const [touched, setTouched] = useState(false);

  // Reset on open, not on close: leaving the previous reason in the field
  // makes it far too easy to reverse a second allocation with the first one's
  // justification, which is a plausible-looking and wrong audit record.
  useEffect(() => {
    if (open) {
      setReason("");
      setReasonCode(reasonCodes?.[0]?.value ?? "");
      setExtra("");
      setTouched(false);
    }
  }, [open, reasonCodes]);

  const tooShort = reason.trim().length < MIN_REASON_LENGTH;
  const showError = touched && tooShort;

  // Shown always when no code gates it; otherwise only for that code.
  const extraApplies =
    !!extraField &&
    (!extraField.requiredForReasonCode ||
      extraField.requiredForReasonCode === reasonCode);
  const extraMissing = extraApplies && extra.trim().length === 0;

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onCancel}
      fullWidth
      maxWidth="sm"
      aria-labelledby="randomization-reason-dialog-title"
    >
      <DialogTitle id="randomization-reason-dialog-title">{title}</DialogTitle>
      <DialogContent>
        {body ? <DialogContentText sx={{ mb: 2 }}>{body}</DialogContentText> : null}
        {detail ? <Box sx={{ mb: 2 }}>{detail}</Box> : null}

        {reasonCodes && reasonCodes.length > 0 ? (
          <FormControl sx={{ ...InputWrapper, mb: 2 }}>
            <FormLabel sx={LabelStyle} required>
              {t("ui.participant.reverseReasonCode")}
            </FormLabel>
            <TextField
              select
              size="small"
              fullWidth
              value={reasonCode}
              onChange={(event) => setReasonCode(event.target.value)}
              disabled={submitting}
            >
              {reasonCodes.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
        ) : null}

        <FormControl sx={{ ...InputWrapper, mb: extraApplies ? 2 : 0 }}>
          <FormLabel sx={LabelStyle} required>
            {reasonLabel}
          </FormLabel>
          <TextField
            multiline
            minRows={3}
            fullWidth
            size="small"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            onBlur={() => setTouched(true)}
            disabled={submitting}
            error={showError}
            helperText={
              showError ? t("ui.participant.reasonMinLength") : `${reason.trim().length}/${MIN_REASON_LENGTH}`
            }
            inputProps={{ maxLength: 1000 }}
          />
        </FormControl>

        {extraApplies && extraField ? (
          <FormControl sx={InputWrapper}>
            <FormLabel sx={LabelStyle} required>
              {extraField.label}
            </FormLabel>
            <TextField
              size="small"
              fullWidth
              value={extra}
              onChange={(event) => setExtra(event.target.value)}
              onBlur={() => setTouched(true)}
              disabled={submitting}
              error={touched && extraMissing}
              helperText={extraField.helperText}
              inputProps={{ maxLength: extraField.maxLength ?? 200 }}
            />
          </FormControl>
        ) : null}

        {errorMessage ? (
          <Typography variant="body2" color="error" sx={{ mt: 2 }}>
            {errorMessage}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={submitting} color="inherit">
          {t("ui.cancel")}
        </Button>
        <Button
          variant="contained"
          // Disabled while in flight, so a double click cannot act twice
          // (spec 21.8). The Idempotency-Key is the second line of defence.
          disabled={submitting || tooShort || extraMissing}
          onClick={() =>
            onConfirm({
              reason: reason.trim(),
              reasonCode: reasonCodes && reasonCodes.length > 0 ? reasonCode : undefined,
              extra: (extraApplies && extra.trim()) || undefined,
            })
          }
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
